/**
 * ActionMenu Component
 * Reusable dropdown menu for table row actions — and, with a custom
 * `trigger` and `size="touch"`, for full-size header menus on phones.
 */

import { useState, useRef, useEffect, ReactNode } from 'react';
import { MoreVertical } from 'lucide-react';

export interface ActionMenuItem {
    label: string;
    icon?: ReactNode;
    /** Optional second line under the label (shown in `touch` menus and compact ones alike). */
    description?: string;
    onClick: () => void;
    variant?: 'default' | 'danger' | 'success' | 'warning';
    disabled?: boolean;
    divider?: boolean;
}

export type ActionMenuSize = 'compact' | 'touch';

interface ActionMenuProps {
    items: ActionMenuItem[];
    triggerIcon?: ReactNode;
    /** Replaces the ⋮ icon button's content (e.g. icon + "Utilities"). */
    trigger?: ReactNode;
    /** Replaces the ⋮ icon button's classes when `trigger` is used. */
    triggerClassName?: string;
    triggerAriaLabel?: string;
    /** `touch`: 48px rows, larger text and a wider panel for fingers. */
    size?: ActionMenuSize;
    className?: string;
    menuClassName?: string;
    position?: 'left' | 'right';
}

const variantStyles = {
    default: 'text-gray-700 hover:bg-gray-50',
    danger: 'text-red-600 hover:bg-red-50',
    success: 'text-green-600 hover:bg-green-50',
    warning: 'text-orange-600 hover:bg-orange-50',
};

const DEFAULT_TRIGGER_CLASS = 'p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors';

const SIZE_STYLES: Record<ActionMenuSize, { panel: string; item: string; icon: string }> = {
    compact: { panel: 'py-1 min-w-[140px]', item: 'gap-2 px-4 py-2 text-sm', icon: '' },
    touch: {
        panel: 'py-2 w-72 max-w-[calc(100vw-2rem)]',
        item: 'gap-3 px-4 py-3 min-h-12 text-base',
        icon: 'flex w-5 shrink-0 justify-center',
    },
};

/**
 * ActionMenu - A reusable dropdown menu for actions
 *
 * @example
 * <ActionMenu
 *   items={[
 *     { label: 'Edit', icon: <Edit size={16} />, onClick: handleEdit },
 *     { label: 'Delete', icon: <Trash size={16} />, onClick: handleDelete, variant: 'danger' },
 *   ]}
 * />
 */
export function ActionMenu({
    items,
    triggerIcon,
    trigger,
    triggerClassName,
    triggerAriaLabel = 'Actions menu',
    size = 'compact',
    className = '',
    menuClassName = '',
    position = 'right',
}: ActionMenuProps) {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const sizeStyles = SIZE_STYLES[size];

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Close menu on Escape key
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen]);

    const handleItemClick = (item: ActionMenuItem) => {
        if (!item.disabled) {
            item.onClick();
            setIsOpen(false);
        }
    };

    return (
        <div ref={menuRef} className={`relative ${className}`}>
            {/* Trigger Button */}
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={trigger ? (triggerClassName ?? DEFAULT_TRIGGER_CLASS) : DEFAULT_TRIGGER_CLASS}
                aria-label={triggerAriaLabel}
                aria-haspopup="menu"
                aria-expanded={isOpen}
            >
                {trigger ?? triggerIcon ?? <MoreVertical size={18} />}
            </button>

            {/* Menu Dropdown */}
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 z-10"
                        onClick={() => setIsOpen(false)}
                    />

                    {/* Menu */}
                    <div
                        role="menu"
                        className={`
                            absolute ${position === 'right' ? 'right-0' : 'left-0'} top-full mt-1
                            z-20 bg-white rounded-lg shadow-lg border border-gray-200
                            ${sizeStyles.panel}
                            ${menuClassName}
                        `.trim()}
                    >
                        {items.map((item, index) => (
                            <div key={index}>
                                {item.divider && index > 0 && (
                                    <hr className="my-1 border-gray-200" />
                                )}
                                <button
                                    type="button"
                                    role="menuitem"
                                    onClick={() => handleItemClick(item)}
                                    disabled={item.disabled}
                                    className={`
                                        flex items-center w-full text-left
                                        ${sizeStyles.item}
                                        ${variantStyles[item.variant || 'default']}
                                        ${item.disabled ? 'opacity-50 cursor-not-allowed' : ''}
                                    `.trim()}
                                >
                                    {item.icon && (sizeStyles.icon ? <span className={sizeStyles.icon}>{item.icon}</span> : item.icon)}
                                    {item.description ? (
                                        <span className="flex min-w-0 flex-col">
                                            <span className="font-medium">{item.label}</span>
                                            <span className="text-xs text-gray-500">{item.description}</span>
                                        </span>
                                    ) : (
                                        item.label
                                    )}
                                </button>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

export default ActionMenu;
